import React from "react";
import { ErrorPage } from "views/error";
import rollbar from "helpers/rollbar";
import store from "store/store";
import { ThemeOptions } from "store/view/types";

interface ErrorBoundaryProps {
    children?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
}

/**
 * Top-level error boundary. Mounted ABOVE `BaseLayout` / `DashboardLayout` /
 * `OnboardingWrapper` (see App.tsx), never inside them - on purpose.
 *
 * Why: `<Loading />` (the fullscreen loading overlay) is rendered inside
 * those layouts, not at the app root. The dashboard has several places that
 * increment the global loading counter (`store/loading/slice.ts`) without a
 * matching decrement on the error path (no `finally`), so an unhandled
 * error can leave that overlay stuck on indefinitely. Unmounting the whole
 * layout subtree when we catch an error takes `<Loading />` down with it,
 * so the error page is never rendered underneath a stuck overlay.
 *
 * A React error boundary also doesn't reset itself on route changes, so the
 * `ErrorPage` rendered here (variant="error") deliberately uses hard
 * navigation (`<a href>` for its own nav strip, `window.location.reload()` /
 * `window.location.assign()` for its actions) instead of react-router. A
 * full page reload is what actually clears any global state left dirty by
 * the error - a client-side `navigate()` would just remount the layout on
 * top of the same stale state.
 */
export class ErrorBoundary extends React.Component<
    ErrorBoundaryProps,
    ErrorBoundaryState
> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false };
    }

    static getDerivedStateFromError(): ErrorBoundaryState {
        return { hasError: true };
    }

    componentDidCatch(error: Error, info: React.ErrorInfo): void {
        rollbar.error("Unhandled error caught by ErrorBoundary", error, {
            componentStack: info.componentStack
        });

        // `AppWrapper` (the component that normally applies `data-theme` via
        // `useTheme`) is inside this boundary and gets unmounted along with
        // everything else when we catch, so it never gets a chance to run.
        // Without this, a dark-theme user would see the error page rendered
        // in light mode (the `:root` default in styles/_themes.scss).
        // Replicate its theme selection here, reading the store directly
        // (same pattern as helpers/rollbar.ts) since there's no live hook
        // context to rely on once the app tree below us has unmounted.
        const themeFromState = store.getState().view?.theme;
        const theme =
            themeFromState === ThemeOptions.Light ||
            themeFromState === ThemeOptions.Dark
                ? themeFromState
                : window.matchMedia("(prefers-color-scheme: dark)").matches
                  ? ThemeOptions.Dark
                  : ThemeOptions.Light;
        document.documentElement.setAttribute("data-theme", theme);
    }

    render(): React.ReactNode {
        if (this.state.hasError) {
            return <ErrorPage variant="error" />;
        }

        return this.props.children;
    }
}
